import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, AlertCircle, HelpCircle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/cn";
import api from "@/lib/api";
import { useSystemHealth, type HealthColor } from "@/hooks/useSystemHealth";

/**
 * Floating system-health pill, top-right of every page.
 *
 * Three states the user actually cares about:
 *   green  — every integration synced inside its TTL
 *   amber  — partial / stale; some data on screen may be old
 *   red    — at least one integration's most recent sync FAILED;
 *            data on screen is likely wrong
 *
 * Click expands a panel with per-integration breakdown + the list of
 * specific records that failed (so Brett can see exactly which Jira
 * card's status didn't sync, since he reports off this data).
 *
 * Sits in a fixed corner rather than inside TopBar because TopBar is
 * per-page; the health signal needs to be present on every screen.
 */

const COLOR_STYLES: Record<HealthColor, { dot: string; ring: string; bg: string; text: string; label: string }> = {
  green: {
    dot: "bg-emerald-500",
    ring: "ring-emerald-200 dark:ring-emerald-500/30",
    bg: "bg-emerald-50 dark:bg-emerald-500/10",
    text: "text-emerald-700 dark:text-emerald-300",
    label: "Healthy",
  },
  amber: {
    dot: "bg-amber-500",
    ring: "ring-amber-200 dark:ring-amber-500/30",
    bg: "bg-amber-50 dark:bg-amber-500/10",
    text: "text-amber-700 dark:text-amber-300",
    label: "Stale data",
  },
  red: {
    dot: "bg-rose-500",
    ring: "ring-rose-200 dark:ring-rose-500/30",
    bg: "bg-rose-50 dark:bg-rose-500/10",
    text: "text-rose-700 dark:text-rose-300",
    label: "Sync failure",
  },
  unknown: {
    dot: "bg-slate-400",
    ring: "ring-line",
    bg: "bg-canvas",
    text: "text-ink-3",
    label: "Unknown",
  },
};

const ICONS: Record<HealthColor, typeof CheckCircle2> = {
  green: CheckCircle2,
  amber: AlertTriangle,
  red: AlertCircle,
  unknown: HelpCircle,
};

function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  const t = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z").getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

/**
 * Variant: 'sidebar' renders a dark-themed row that fits the sidebar
 * footer (default app placement, never collides with page actions).
 * 'floating' is the original top-right pill, kept available for any
 * future surface that needs an inline indicator.
 */
interface IndicatorProps {
  variant?: "sidebar" | "floating";
}

export function HealthIndicator({ variant = "sidebar" }: IndicatorProps = {}) {
  const { data, isLoading } = useSystemHealth();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const status: HealthColor = data?.status ?? "unknown";
  const styles = COLOR_STYLES[status];
  const Icon = ICONS[status];

  // Pulse the dot only when something is genuinely wrong — green stays
  // calm so it isn't visual noise.
  const shouldPulse = status === "red";

  // ----- Sidebar variant: dark row + popover that opens to the RIGHT
  // (so it appears OVER the page content rather than off-screen left).
  if (variant === "sidebar") {
    return (
      <div className="relative" ref={popoverRef}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-ink-subtle transition-colors hover:bg-slate-800 hover:text-ink-ghost"
          title={data?.summary ?? "Loading system health…"}
          aria-label="System health"
        >
          <span className="relative flex h-2 w-2">
            {shouldPulse && (
              <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-75", styles.dot)} />
            )}
            <span className={cn("relative inline-flex h-2 w-2 rounded-full", styles.dot)} />
          </span>
          <span className="flex-1 text-left">
            {isLoading ? "Checking…" : `Sync: ${styles.label}`}
          </span>
        </button>

        <AnimatePresence>
          {open && data && (
            <motion.div
              initial={{ opacity: 0, x: -4, scale: 0.98 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -4, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              // Anchor to the sidebar item, open to the right so the
              // panel sits over the page content (sidebar is fixed-width
              // 14rem; panel is 24rem, lands cleanly on the page area).
              className="absolute bottom-0 left-full z-50 ml-2 w-96 overflow-hidden rounded-xl bg-surface text-ink-2 shadow-xl ring-1 ring-line"
            >
              <PopoverBody data={data} status={status} Icon={Icon} styles={styles} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ----- Floating variant (legacy)
  return (
    <div className="fixed right-4 top-3 z-50" ref={popoverRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium shadow-sm ring-1 transition-colors",
          styles.bg,
          styles.ring,
          styles.text,
          "hover:bg-opacity-80",
        )}
        title={data?.summary ?? "Loading system health…"}
        aria-label="System health"
      >
        <span className="relative flex h-2 w-2">
          {shouldPulse && (
            <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-75", styles.dot)} />
          )}
          <span className={cn("relative inline-flex h-2 w-2 rounded-full", styles.dot)} />
        </span>
        <span className="hidden sm:inline">{isLoading ? "Checking…" : styles.label}</span>
      </button>

      <AnimatePresence>
        {open && data && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-96 overflow-hidden rounded-xl bg-surface shadow-xl ring-1 ring-line"
          >
            <PopoverBody data={data} status={status} Icon={Icon} styles={styles} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Extracted so both variants render the identical body content.
interface PopoverBodyProps {
  data: NonNullable<ReturnType<typeof useSystemHealth>["data"]>;
  status: HealthColor;
  Icon: typeof CheckCircle2;
  styles: (typeof COLOR_STYLES)[HealthColor];
}

function PopoverBody({ data, Icon, styles }: PopoverBodyProps) {
  return (
    <>
      <div className="flex items-start gap-3 border-b border-line-soft px-4 py-3">
        <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", styles.text)} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">{styles.label}</p>
          <p className="mt-0.5 text-xs text-ink-muted">{data.summary}</p>
        </div>
      </div>

      <div className="divide-y divide-line-soft">
        {data.integrations.map((i) => {
          const s = COLOR_STYLES[i.status];
          return (
            <div key={i.integration} className="px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className={cn("inline-flex h-2 w-2 rounded-full", s.dot)} />
                  <span className="text-sm font-medium capitalize text-ink">
                    {i.integration}
                  </span>
                  {!i.configured && (
                    <span className="rounded-full bg-raised px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-muted">
                      Off
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-[11px] text-ink-muted">
                    {i.last_success_at ? `synced ${formatRelative(i.last_success_at)}` : "no data"}
                  </span>
                  {i.configured && <RefreshButton integration={i.integration} />}
                </div>
              </div>
              {i.reason && <p className="mt-1 text-xs text-ink-muted">{i.reason}</p>}
              {/* Show the actual captured exception text when something is
                  currently wrong. Suppressed on green so a long-since-fixed
                  error from days ago doesn't keep shouting. */}
              {i.status !== "green" && i.last_error_summary && (
                <div className="mt-1.5 rounded border border-rose-100 dark:border-rose-500/30 bg-rose-50/60 dark:bg-rose-500/10 px-2 py-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">
                    Last error{i.last_error_at && ` · ${formatRelative(i.last_error_at)}`}
                  </p>
                  <p className="mt-0.5 break-words text-[11px] text-rose-800 dark:text-rose-300">
                    {i.last_error_summary}
                  </p>
                </div>
              )}
              {i.configured && (i.attempts_24h > 0 || i.failures_24h > 0) && (
                <p className="mt-1 text-[11px] text-ink-subtle tabular-nums">
                  Last 24h: {i.attempts_24h} attempts
                  {i.failures_24h > 0 && `, ${i.failures_24h} failed`}
                  {i.records_failed_24h > 0 && `, ${i.records_failed_24h} records affected`}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {data.failed_records.length > 0 && (
        <div className="border-t border-line-soft bg-canvas/50 px-4 py-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
            Records currently failing ({data.failed_records.length})
          </p>
          <ul className="max-h-40 space-y-1 overflow-y-auto">
            {data.failed_records.slice(0, 10).map((f) => (
              <li key={`${f.integration}-${f.target}`} className="text-xs text-ink-3">
                <span className="font-mono text-rose-700 dark:text-rose-300">{f.target}</span>
                {f.error && <span className="text-ink-muted"> — {f.error}</span>}
              </li>
            ))}
            {data.failed_records.length > 10 && (
              <li className="text-[11px] text-ink-subtle">
                …and {data.failed_records.length - 10} more
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="border-t border-line-soft px-4 py-2 text-[11px] text-ink-subtle">
        Checked {formatRelative(data.checked_at)} · auto-refreshes every minute
      </div>
    </>
  );
}

/**
 * Per-integration manual refresh. Hits POST /system/refresh/{integration},
 * which reuses the same code path a user-driven sync would (so the sync_log
 * entry and recorded outcome are identical). On completion we invalidate
 * the system-health query so the popover re-pulls and reflects the new
 * last_success_at / last_error_summary immediately.
 *
 * Result toast lives inline (not a global snackbar) so the user sees
 * feedback in the same panel that triggered the action.
 */
function RefreshButton({ integration }: { integration: string }) {
  const qc = useQueryClient();
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const r = await api.post(`/system/refresh/${integration}`);
      return r.data as { status: string; message: string };
    },
    onSuccess: (data) => {
      setResult({ ok: data.status === "ok", msg: data.message });
      qc.invalidateQueries({ queryKey: ["system", "health"] });
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Refresh failed for an unknown reason.";
      setResult({ ok: false, msg });
    },
  });

  // Auto-clear the toast after a few seconds so it doesn't pile up if
  // the user clicks repeatedly.
  useEffect(() => {
    if (!result) return;
    const t = setTimeout(() => setResult(null), 5000);
    return () => clearTimeout(t);
  }, [result]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          mutation.mutate();
        }}
        disabled={mutation.isPending}
        className={cn(
          "flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-line transition-colors",
          mutation.isPending
            ? "cursor-wait bg-canvas text-ink-subtle"
            : "bg-surface text-ink-3 hover:bg-raised hover:text-ink",
        )}
        aria-label={`Refresh ${integration} now`}
      >
        <RefreshCw
          className={cn("h-3 w-3", mutation.isPending && "animate-spin")}
        />
        {mutation.isPending ? "Syncing…" : "Refresh"}
      </button>
      {result && (
        <p
          className={cn(
            "absolute right-0 top-full z-10 mt-1 max-w-[18rem] whitespace-normal break-words rounded-md px-2 py-1 text-[10px] shadow-md ring-1",
            result.ok
              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30"
              : "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-rose-200 dark:ring-rose-500/30",
          )}
        >
          {result.msg}
        </p>
      )}
    </div>
  );
}
